/**
 * 温馨治愈风格菜单管理页面
 * 使用柔和配色、圆角、轻柔动画
 */

import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Card,
  Form,
  Input,
  Modal,
  message,
  Tag,
  Switch,
  Tree,
  Row,
  Col,
  Select,
  Typography
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MenuOutlined,
  SearchOutlined,
  UpOutlined,
  DownOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  FolderOutlined,
  FileTextOutlined,
  PushpinOutlined
} from '@ant-design/icons';
import {
  getMenuList,
  createMenu,
  updateMenu,
  deleteMenu,
  getMenuById
} from '../services/api/menu';
import { MenuType, MenuStatus, MenuVisible } from '../types/menu';
import { useModal } from '../hooks';
import SearchBar from '../components/common/SearchBar';
import EmptyWarm from '../components/common/EmptyWarm';
import LoadingWarm from '../components/common/LoadingWarm';
import './MenuManager.css';

const { TextArea } = Input;
const { TreeNode } = Tree;
const { Option } = Select;

const MenuManager: React.FC = () => {
  const modal = useModal();
  const [form] = Form.useForm();
  const [menus, setMenus] = useState<any[]>([]);
  const [treeData, setTreeData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingMenu, setEditingMenu] = useState<any>(null);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  // 获取菜单列表并构造成树形结构（最多二级）
  const fetchMenus = async () => {
    setLoading(true);
    try {
      const response = await getMenuList();
      console.log(' getMenuList 原始响应:', response);

      // 确保获取到数组
      let list = response;
      if (response && Array.isArray(response.records)) {
        list = response.records;
      } else if (response && !Array.isArray(response)) {
        console.warn('响应不是数组，尝试访问 data 或 list:', response);
        list = response.data || response.list || [];
      }

      console.log('处理后的菜单列表:', list);

      if (!Array.isArray(list)) {
        console.error('菜单列表不是数组:', typeof list);
        message.error('获取菜单列表失败：数据格式错误');
        setLoading(false);
        return;
      }

      // 过滤掉按钮类型（type=2）
      const filteredList = list.filter(menu => menu.type !== 2);
      console.log('过滤后的菜单列表:', filteredList);

      // 手动设置 parentId，根据后端返回的顺序推断
      // 系统管理 (id=1) 和 日志管理 (id=22) 是顶级目录
      const topLevelIds = [1, 22]; // 这些是 type=0 的顶级目录
      const menusWithParentId = filteredList.map(menu => {
        if (menu.type === 1) {
          // 二级菜单，默认分配给系统管理目录（除非是日志管理下的菜单）
          if ([23, 27].includes(menu.id)) { // 操作日志和登录日志属于日志管理
            return { ...menu, parentId: 22 };
          } else {
            return { ...menu, parentId: 1 }; // 其他二级菜单属于系统管理
          }
        }
        return menu;
      });

      setMenus(menusWithParentId);

      // 构造树形结构（最多二级）
      const buildTree = (parentId: number | null = null, level = 0) => {
        // 只允许最多二级
        if (level >= 2) {
          return [];
        }

        return menusWithParentId
          .filter(menu => {
            // 顶级目录：没有 parentId 或 parentId 为 null/undefined/0
            if (parentId === null) {
              return menu.parentId === null || menu.parentId === undefined || menu.parentId === 0;
            }
            // 子菜单：parentId 匹配
            return menu.parentId === parentId;
          })
          .map(menu => {
            const children = buildTree(menu.id, level + 1);
            return {
              ...menu,
              children: children.length > 0 ? children : undefined
            };
          });
      };

      const tree = buildTree(null);
      console.log('构建的树形结构:', tree);
      setTreeData(tree);

      // 设置展开的节点 - 只展开有子节点的目录项（一级目录）
      const keys: string[] = [];
      const collectKeys = (nodes: any[]) => {
        nodes.forEach(node => {
          console.log('遍历节点:', { id: node.id, name: node.name, type: node.type, hasChildren: node.children && node.children.length > 0, childrenCount: node.children?.length });
          // 只添加有子节点的目录到展开列表
          if (node.type === 0 && node.children && node.children.length > 0) {
            keys.push(node.id.toString());
            console.log('添加到展开列表:', node.id);
          }
        });
      };
      collectKeys(tree);
      console.log('最终展开的节点 keys:', keys);
      setExpandedKeys(keys);
    } catch (error) {
      console.error('获取菜单列表失败:', error);
      message.error('获取菜单列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMenus();
  }, []);

  // 删除菜单
  const handleDelete = async (menu: any) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除菜单 "${menu.name}" 吗？`,
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await deleteMenu(menu.id);
          message.success('菜单删除成功');
          fetchMenus();
        } catch (error) {
          console.error('删除菜单失败:', error);
          message.error('删除菜单失败');
        }
      }
    });
  };

  // 切换菜单状态（启用/禁用）
  const handleStatusToggle = async (menu: any) => {
    Modal.confirm({
      title: '确认操作',
      content: `确定要${menu.status === 1 ? '禁用' : '启用'}菜单 "${menu.name}" 吗？`,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          await updateMenu(menu.id, {
            ...menu,
            status: menu.status === 1 ? 0 : 1
          });
          message.success('状态更新成功');
          fetchMenus();
        } catch (error) {
          console.error('状态更新失败:', error);
          message.error('状态更新失败');
        }
      }
    });
  };

  // 保存菜单
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      modal.startConfirmLoading();

      // 验证：如果选择的是菜单类型（type=1），必须填写 path
      if (values.type === 1 && !values.path) {
        message.error('菜单类型必须填写路由路径');
        modal.stopConfirmLoading();
        return;
      }

      // 验证：目录类型（type=0）不能有父级（只能是顶级）
      if (values.type === 0 && values.parentId) {
        message.error('目录类型只能作为顶级菜单，不能选择父级');
        modal.stopConfirmLoading();
        return;
      }

      const data = {
        ...values,
        parentId: values.parentId || null,
        visible: values.visible ? 1 : 0,
        status: values.status ? 1 : 0,
        sortOrder: values.sortOrder || 0,
        type: Number(values.type)
      };

      if (editingMenu) {
        await updateMenu(editingMenu.id, data);
        message.success('菜单更新成功');
      } else {
        await createMenu(data);
        message.success('菜单创建成功');
      }

      modal.hideModal();
      form.resetFields();
      setEditingMenu(null);
      fetchMenus();
    } catch (error) {
      console.error('操作失败:', error);
      modal.stopConfirmLoading();
    }
  };

  // 获取父级菜单选项（只允许一级目录作为父级）
  const getParentOptions = () => {
    const options: any[] = [
      { label: '顶级菜单', value: null },
      { label: '─'.repeat(15), value: 'divider', disabled: true }
    ];

    // 遍历一级目录（只有目录才能作为父级）
    treeData.forEach(node => {
      if (node.type === 0) { // 目录
        options.push({
          label: `📁 ${node.name}`,
          value: node.id
        });
      }
    });

    return options;
  };

  // 渲染菜单树节点（只显示目录和菜单，最多二级）
  const renderTreeNodes = (data: any[]) =>
    data.map(item => {
      // 过滤掉按钮类型（理论上不会出现，双重保障）
      if (item.type === 2) return null;

      const iconMap: Record<number, JSX.Element> = {
        0: <FolderOutlined style={{ color: '#FFA726' }} />,
        1: <FileTextOutlined style={{ color: '#42A5F5' }} />
      };

      const statusTag = (
        <Tag color={item.status === 1 ? 'success' : 'default'} style={{ marginLeft: 8 }}>
          {item.status === 1 ? '✅' : '⏸️'}
        </Tag>
      );

      const visibleIcon = item.visible === 1 ? (
        <EyeOutlined style={{ marginLeft: 8, color: '#42A5F5' }} />
      ) : (
        <EyeInvisibleOutlined style={{ marginLeft: 8, color: '#BDBDBD' }} />
      );

      // 菜单项（二级）不再有子节点
      if (item.type === 1) {
        return (
          <TreeNode
            title={
              <span>
                {iconMap[item.type]}
                <span style={{ marginLeft: 8 }}>{item.name}</span>
                {statusTag}
                {visibleIcon}
              </span>
            }
            key={item.id.toString()}
            dataRef={item}
          />
        );
      }

      // 目录可以有子菜单（最多二级）
      if (item.children && item.children.length > 0) {
        return (
          <TreeNode
            title={
              <span>
                {iconMap[item.type]}
                <span style={{ marginLeft: 8 }}>{item.name}</span>
                {statusTag}
                {visibleIcon}
              </span>
            }
            key={item.id.toString()}
            dataRef={item}
          >
            {renderTreeNodes(item.children)}
          </TreeNode>
        );
      }

      // 空目录
      return (
        <TreeNode
          title={
            <span>
              {iconMap[item.type]}
              <span style={{ marginLeft: 8 }}>{item.name}</span>
              {statusTag}
              {visibleIcon}
            </span>
          }
          key={item.id.toString()}
          dataRef={item}
        />
      );
    }).filter(Boolean);

  return (
    <div className="menu-manager-container">
      <Card
        className="warm-card"
        title={
          <Space>
            <MenuOutlined className="menu-manager-title-icon" />
            <span>菜单管理</span>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingMenu(null);
              form.resetFields();
              modal.showModal();
            }}
          >
            🌸 新增菜单
          </Button>
        }
        bordered={false}
      >
        {loading ? (
          <LoadingWarm type="tree" tip="菜单数据正在路上~" />
        ) : (
          <div className="menu-manager-tree">
            {treeData.length === 0 ? (
              <EmptyWarm description="暂无菜单数据" />
            ) : (
              <Tree
                expandedKeys={expandedKeys}
                onExpand={(keys) => setExpandedKeys(keys.map(k => k.toString()))}
                treeData={treeData.map(item => ({
                  title: (
                    <span>
                      {item.type === 0 ? (
                        <FolderOutlined style={{ color: '#FFA726', marginRight: 8 }} />
                      ) : (
                        <FileTextOutlined style={{ color: '#42A5F5', marginRight: 8 }} />
                      )}
                      <span>{item.name}</span>
                      <Tag
                        color={item.status === 1 ? 'success' : 'default'}
                        style={{ marginLeft: 8, cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStatusToggle(item);
                        }}
                      >
                        {item.status === 1 ? '✅' : '⏸️'}
                      </Tag>
                      {item.visible === 1 ? (
                        <EyeOutlined style={{ marginLeft: 8, color: '#42A5F5' }} />
                      ) : (
                        <EyeInvisibleOutlined style={{ marginLeft: 8, color: '#BDBDBD' }} />
                      )}
                    </span>
                  ),
                  key: item.id.toString(),
                  children: item.children && item.children.length > 0
                    ? item.children.map(child => ({
                        title: (
                          <span>
                            <FileTextOutlined style={{ color: '#42A5F5', marginRight: 8 }} />
                            <span>{child.name}</span>
                            <Tag
                              color={child.status === 1 ? 'success' : 'default'}
                              style={{ marginLeft: 8, cursor: 'pointer' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStatusToggle(child);
                              }}
                            >
                              {child.status === 1 ? '✅' : '⏸️'}
                            </Tag>
                            {child.visible === 1 ? (
                              <EyeOutlined style={{ marginLeft: 8, color: '#42A5F5' }} />
                            ) : (
                              <EyeInvisibleOutlined style={{ marginLeft: 8, color: '#BDBDBD' }} />
                            )}
                          </span>
                        ),
                        key: child.id.toString(),
                        dataRef: child
                      }))
                    : undefined,
                  dataRef: item
                }))}
                onSelect={(selectedKeys, info) => {
                  if (selectedKeys.length > 0) {
                    const menu = info.node.dataRef;
                    if (menu && menu.type === 1 && menu.path) {
                      console.log('点击菜单:', menu.path);
                    }
                  }
                }}
              />
            )}
          </div>
        )}
      </Card>

      {/* 新增/编辑菜单模态框 */}
      <Modal
        title={editingMenu ? '✏️ 编辑菜单' : '➕ 新增菜单'}
        open={modal.visible}
        onOk={handleSave}
        onCancel={modal.handleCancel}
        confirmLoading={modal.confirmLoading}
        okText="保存"
        cancelText="取消"
        width={700}
        className="warm-modal"
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="parentId"
                label="📁 父级菜单"
              >
                <Select
                  placeholder="请选择父级菜单"
                  options={getParentOptions()}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="type"
                label="📊 菜单类型"
                rules={[{ required: true, message: '请选择菜单类型!' }]}
              >
                <Select
                  placeholder="请选择菜单类型"
                  onChange={(value) => {
                    // 选择"目录"时，清空 path 和 component
                    if (value === 0) {
                      form.setFieldsValue({
                        path: '',
                        component: '',
                        parentId: null // 目录只能是顶级
                      });
                    }
                  }}
                >
                  <Option value={0}>📁 目录（一级菜单）</Option>
                  <Option value={1}>📄 菜单（二级菜单，点击进入列表页）</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="name"
            label="📝 菜单名称"
            rules={[{ required: true, message: '请输入菜单名称!' }]}
          >
            <Input placeholder="例如：用户管理、角色管理" />
          </Form.Item>

          <Form.Item
            name="path"
            label="🔗 路由路径"
          >
            <Input placeholder="例如：/users、/roles" />
          </Form.Item>

          <Form.Item
            name="component"
            label="🧩 组件路径"
          >
            <Input placeholder="例如：pages/Users、pages/Roles" />
          </Form.Item>

          <Form.Item
            name="perms"
            label="🔐 权限标识"
          >
            <Input placeholder="例如：system:user:list、system:role:add" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="icon"
                label="🎨 图标"
              >
                <Input placeholder="例如：UserOutlined" />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="sortOrder"
                label="🔢 排序"
              >
                <Input type="number" placeholder="数字越小越靠前" />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="visible"
                label="👁️ 可见性"
                valuePropName="checked"
                initialValue={true}
              >
                <Switch
                  checkedChildren="👁️ 显示"
                  unCheckedChildren="🙈 隐藏"
                  defaultChecked
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default MenuManager;
